import React, { Fragment } from 'react';
import {
    Paper,
    List, ListItem, ListItemText, ListItemIcon,
    Typography, Divider, Box, IconButton, Link, Tooltip
} from '@mui/material';
import Icon from '@mdi/react'
import {
    mdiMapMarker, mdiFireplace, mdiCityVariant,
    mdiBarcode, mdiPentagon, mdiDomain,
    mdiRoadVariant, mdiGoogleMaps, mdiContentCopy,
} from '@mdi/js';

const isValidCoordinate = coordinate => (
    coordinate !== null &&
    coordinate !== undefined &&
    coordinate !== '' &&
    Number.isFinite(Number(coordinate))
);

const AddressCard = ({ address, coordinates, title }) => {
    const latitude = coordinates && coordinates.lat;
    const longitude = coordinates && coordinates.lng;
    const hasCoordinates = isValidCoordinate(latitude) && isValidCoordinate(longitude);
    const googleMapLink = hasCoordinates
        ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${latitude},${longitude}`)}`
        : '';

    const copyGoogleMapLink = () => {
        navigator.clipboard.writeText(googleMapLink);
    };

    return (
        <Paper>
            {
                address ?
                    <List dense>
                        <ListItem>
                            <ListItemText
                                primary={
                                    <Typography variant="h6"><b>{title}</b></Typography>
                                }
                            />
                        </ListItem>
                        <Divider />
                        <ListItem>
                            <ListItemIcon>
                                <Icon path={mdiMapMarker} size={1} color="DodgerBlue" />
                            </ListItemIcon>
                            <ListItemText
                                primary={
                                    <Typography variant="body1"><b>Address</b></Typography>
                                }
                                secondary={
                                    <Typography variant="body1" display="inline">{address.Combined}</Typography>
                                }
                            />
                        </ListItem>
                        {
                            address.Road && <ListItem>
                                <ListItemIcon>
                                    <Icon path={mdiRoadVariant} size={1} color="DodgerBlue" />
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Typography variant="body1"><b>Road</b></Typography>
                                    }
                                    secondary={
                                        <Typography variant="body1">{address.Road}</Typography>
                                    }
                                />
                            </ListItem>
                        }
                        {
                            address.Landmark && <ListItem>
                                <ListItemIcon>
                                    <Icon path={mdiFireplace} size={1} color="DodgerBlue" />
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Typography variant="body1"><b>Landmark</b></Typography>
                                    }
                                    secondary={
                                        <Typography variant="body1" display="inline">{address.Landmark}</Typography>
                                    }
                                />
                            </ListItem>
                        }
                        {
                            address.City && <ListItem>
                                <ListItemIcon>
                                    <Icon path={mdiCityVariant} size={1} color="DodgerBlue" />
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Typography variant="body1"><b>City</b></Typography>
                                    }
                                    secondary={
                                        <Typography variant="body1">{address.City}</Typography>
                                    }
                                />
                            </ListItem>

                        }
                        {
                            address.PIN && <ListItem>
                                <ListItemIcon>
                                    <Icon path={mdiBarcode} size={1} color="DodgerBlue" />
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Typography variant="body1"><b>PIN</b></Typography>
                                    }
                                    secondary={
                                        <Typography variant="body1">{address.PIN}</Typography>
                                    }
                                />
                            </ListItem>
                        }
                        {
                            (address.Zone || address.Marker) && <Fragment>

                                <ListItem>
                                    <ListItemText
                                        primary={
                                            <Typography variant="h6"><b>My Places</b></Typography>
                                        }
                                    />
                                </ListItem>
                                <Divider />
                                {
                                    address.Zone && <ListItem>
                                        <ListItemIcon>
                                            <Icon path={mdiPentagon} size={1} color="DodgerBlue" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <Typography variant="body1"><b>Zone</b></Typography>
                                            }
                                            secondary={
                                                <Typography variant="body1">{address.Zone}</Typography>
                                            }
                                        />
                                    </ListItem>
                                }
                                {
                                    address.Marker && <ListItem>
                                        <ListItemIcon>
                                            <Icon path={mdiDomain} size={1} color="DodgerBlue" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <Typography variant="body1"><b>Marker</b></Typography>
                                            }
                                            secondary={
                                                <Typography variant="body1">{address.Marker}</Typography>
                                            }
                                        />
                                    </ListItem>

                                }
                            </Fragment>
                        }
                        {
                            hasCoordinates && <Fragment>
                                <ListItem>
                                    <ListItemText
                                        primary={
                                            <Typography variant="h6"><b>Google Map Link</b></Typography>
                                        }
                                    />
                                </ListItem>
                                <Divider />
                                <ListItem alignItems="flex-start">
                                    <ListItemIcon>
                                        <Icon path={mdiGoogleMaps} size={1} color="DodgerBlue" />
                                    </ListItemIcon>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="body1">
                                            <b>Latitude:</b> {latitude}
                                        </Typography>
                                        <Typography variant="body1">
                                            <b>Longitude:</b> {longitude}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                            <Link
                                                href={googleMapLink}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}
                                            >
                                                {googleMapLink}
                                            </Link>
                                            <Tooltip title="Copy Google Maps link">
                                                <IconButton
                                                    size="small"
                                                    aria-label="Copy Google Maps link"
                                                    onClick={copyGoogleMapLink}
                                                    sx={{ ml: 0.5, flexShrink: 0 }}
                                                >
                                                    <Icon path={mdiContentCopy} size={0.7} />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                </ListItem>
                            </Fragment>
                        }
                    </List>
                    :
                    <h2>Loading</h2>
            }
        </Paper>
    );
}

export default AddressCard;
